import clsx from "clsx";
import type { ComponentPropsWithoutRef, ReactNode } from "react";

import {
  type AnalyticsAttrs,
  pickAnalyticsAttrs,
} from "../../../_components/ui/analytics-attrs";
import { Link } from "../../../_components/ui/link";

/**
 * 外枠 min-h-48 + 内側 pill min-h-36 の二重構造。 タップ領域 48px はデザイナー指針
 * (SP 推奨)、 視覚的サイズはピル側で表現する。 href ありなら <Link>、 無しなら <button>。
 */
type DxButtonVariant = "solid" | "ghost" | "transparent";

type CommonProps = AnalyticsAttrs & {
  variant?: DxButtonVariant;
  iconBefore?: ReactNode;
  iconAfter?: ReactNode;
  children: ReactNode;
  className?: string;
};

type ButtonModeProps = CommonProps & {
  href?: undefined;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
  onClick?: ComponentPropsWithoutRef<"button">["onClick"];
  ariaLabel?: string;
};

type LinkModeProps = CommonProps & {
  href: string;
  target?: string;
  rel?: string;
  ariaLabel?: string;
};

export type DxButtonProps = ButtonModeProps | LinkModeProps;

const TOUCH_BASE =
  "group inline-flex items-center justify-center min-h-[48px] " +
  "focus-visible:outline-none focus:outline-none " +
  "disabled:cursor-not-allowed";

const PILL_BASE =
  "inline-flex items-center justify-center gap-[4px] " +
  "min-w-[72px] px-[12px] py-[6px] rounded-full " +
  "dx-body-2-bold whitespace-nowrap " +
  "transition-colors duration-150 " +
  "group-focus-visible:ring-[1.5px] group-focus-visible:ring-dx-border-focus";

// disabled は全 variant 共通の stained 色 (variant ごとに違うと UX 一貫性が崩れる)。
const PILL_VARIANT: Record<DxButtonVariant, string> = {
  solid:
    "bg-dx-bg-inverse text-dx-text-inverse group-hover:bg-dx-bg-inverse-hover group-disabled:bg-dx-surface-subtle group-disabled:text-dx-text-muted",
  ghost:
    "bg-dx-surface-base border border-dx-text-muted text-dx-text-default group-hover:bg-dx-bg-base-hover group-disabled:bg-dx-surface-base group-disabled:text-dx-text-muted",
  transparent:
    "bg-transparent text-dx-text-default group-hover:bg-dx-bg-base-hover group-disabled:bg-transparent group-disabled:text-dx-text-muted",
};

function PillInner({
  variant,
  iconBefore,
  iconAfter,
  children,
}: {
  variant: DxButtonVariant;
  iconBefore?: ReactNode;
  iconAfter?: ReactNode;
  children: ReactNode;
}) {
  return (
    <span className={clsx(PILL_BASE, PILL_VARIANT[variant])}>
      {iconBefore !== undefined && (
        <span className="inline-flex size-[20px] shrink-0 items-center justify-center">
          {iconBefore}
        </span>
      )}
      <span className="inline-flex items-center justify-center">
        {children}
      </span>
      {iconAfter !== undefined && (
        <span className="inline-flex size-[20px] shrink-0 items-center justify-center">
          {iconAfter}
        </span>
      )}
    </span>
  );
}

export function DxButton(props: DxButtonProps) {
  const variant: DxButtonVariant = props.variant ?? "solid";
  const inner = (
    <PillInner
      variant={variant}
      iconBefore={props.iconBefore}
      iconAfter={props.iconAfter}
    >
      {props.children}
    </PillInner>
  );

  if (props.href !== undefined) {
    return (
      <Link
        {...pickAnalyticsAttrs(props)}
        href={props.href}
        target={props.target}
        rel={props.rel}
        aria-label={props.ariaLabel}
        className={clsx(TOUCH_BASE, props.className)}
      >
        {inner}
      </Link>
    );
  }
  return (
    <button
      {...pickAnalyticsAttrs(props)}
      type={props.type ?? "button"}
      disabled={props.disabled}
      onClick={props.onClick}
      aria-label={props.ariaLabel}
      className={clsx(TOUCH_BASE, props.className)}
    >
      {inner}
    </button>
  );
}
